/**
 * AI credits — the shared math and row shapes for the two-wallet (TEAM/USER)
 * credit system layered on top of AiUsage token tracking. This module only
 * defines the pure cost function, the versioned rate shape and its code
 * fallback; the DynamoDB-backed rate lookup and the wallet/ledger stores are
 * built separately (see apps/api/ARCHITECTURE.md § "AI credits").
 *
 * Credits are always stored and moved as INTEGER micro-credits (µC) —
 * `MICRO_CREDITS_PER_CREDIT` per credit — so the data layer never holds a
 * float. `formatMicroCreditsAsCredits` converts to a decimal credits number
 * for display only.
 */

import log from 'loglevel';

import { AiModelFamily, resolveModelFamily } from './aiUsage';

export enum CreditWalletKind {
  TEAM = 'TEAM',
  USER = 'USER',
}

export enum CreditLedgerKind {
  GRANT_PLAN = 'GRANT_PLAN',
  PURCHASE = 'PURCHASE',
  TRANSFER_IN = 'TRANSFER_IN',
  TRANSFER_OUT = 'TRANSFER_OUT',
  DEBIT = 'DEBIT',
  ADJUSTMENT = 'ADJUSTMENT',
  /** Wipes whatever was left of a plan period's allowance at renewal — see docs/AI_CREDITS_PLAN.md § 3b. Always paired with a GRANT_PLAN line for the new period. */
  EXPIRE_PLAN = 'EXPIRE_PLAN',
}

export enum CreditStatementRowKind {
  /** One row per FlowExecution/AgentRun — every debit of that execution folds in. */
  EXECUTION = 'EXECUTION',
  /** One row per single money movement — a purchase, a plan grant/expiry, an adjustment, a transfer. */
  MOVEMENT = 'MOVEMENT',
}

export const MICRO_CREDITS_PER_CREDIT = 1_000_000;

/**
 * Working value from docs/AI_CREDITS_PLAN.md § 3b/§5 — a US$30/month seat grants this many
 * plan credits per billing period. The real allowance lives on the Stripe price
 * (`credits_per_seat` metadata) and is read at renewal; this is only the fallback when that
 * metadata is absent, so it stays a named constant rather than a literal at every call site.
 */
export const DEFAULT_PLAN_CREDITS_PER_SEAT = 3_000;
export const DEFAULT_PLAN_ALLOWANCE_MICRO_CREDITS =
  DEFAULT_PLAN_CREDITS_PER_SEAT * MICRO_CREDITS_PER_CREDIT;

/**
 * Metadata key on the Stripe Price object holding the per-seat monthly credit
 * allowance (a whole-credits string, e.g. `'3000'`) — read at every
 * invoice-triggered grant, with `DEFAULT_PLAN_ALLOWANCE_MICRO_CREDITS` as the
 * fallback when it's absent or unparsable.
 */
export const CREDITS_PER_SEAT_METADATA_KEY = 'credits_per_seat';

/**
 * The weighting factors applied to raw token counts before the per-family
 * multiplier — i.e. the *1 / *5 / *0.1 / *1.25 constants from the credit
 * formula, named here instead of inlined at the call site.
 */
export interface AiCreditModelRates {
  inputRate: number;
  outputRate: number;
  cacheReadRate: number;
  cacheWriteRate: number;
}

export type AiCreditFamilyMultipliers = Record<AiModelFamily, number>;

/**
 * Per-family override of `modelRates.outputRate`, defaulting to it when a family has no
 * entry here. JEV (typesafe.ai's decision model, see `aiExternalModels.ts`) is the only
 * override today: it is priced input-only on the USD side, so weighting its output
 * tokens at the default 5x would charge credits for a dimension that costs nothing.
 */
export type AiCreditOutputWeightOverrides = Partial<
  Record<AiModelFamily, number>
>;

/**
 * A row loaded from the (later) DynamoDB rate config table, versioned by
 * effective date. `DEFAULT_AI_CREDIT_RATES` below is the code fallback used
 * when no row is loaded — this module resolves nothing from DynamoDB itself.
 */
export interface AiCreditRateConfig {
  modelRates: AiCreditModelRates;
  familyMultipliers: AiCreditFamilyMultipliers;
  outputWeightOverrides: AiCreditOutputWeightOverrides;
  /** ISO date the config took effect. */
  effectiveFrom: string;
  version: string;
}

/** 1 credit ≈ 1k Sonnet input tokens: weighted tokens are divided by this before the family multiplier applies. */
const WEIGHTED_TOKENS_PER_CREDIT = 1000;

export const DEFAULT_AI_CREDIT_RATES: AiCreditRateConfig = {
  modelRates: {
    inputRate: 1,
    outputRate: 5,
    cacheReadRate: 0.1,
    cacheWriteRate: 1.25,
  },
  familyMultipliers: {
    [AiModelFamily.OPUS]: 1.5,
    [AiModelFamily.SONNET]: 1,
    [AiModelFamily.HAIKU]: 0.5,
    [AiModelFamily.NOVA]: 0.1,
    [AiModelFamily.TITAN_EMBED]: 0,
    // Placeholder product decision, debit off (see docs/AI_CREDITS_PLAN.md).
    [AiModelFamily.JEV]: 0.1,
    [AiModelFamily.UNKNOWN]: 1,
  },
  outputWeightOverrides: {
    [AiModelFamily.JEV]: 0,
  },
  effectiveFrom: '1970-01-01T00:00:00.000Z',
  version: 'default',
};

export interface AiCreditCostInput {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  modelId: string;
}

/**
 * Returns the INTEGER micro-credit cost of one model invocation. Rounds
 * (rather than truncates) the final µC value, so cost accumulated over many
 * small calls neither systematically over- nor under-counts.
 */
export const calculateCreditCost = (
  input: AiCreditCostInput,
  rates: AiCreditRateConfig,
): number => {
  const family = resolveModelFamily(input.modelId);
  const multiplier =
    rates.familyMultipliers[family] ??
    rates.familyMultipliers[AiModelFamily.UNKNOWN];

  if (family === AiModelFamily.UNKNOWN) {
    log.warn('calculateCreditCost: unknown model family, billed at 1x', {
      modelId: input.modelId,
    });
  }

  const outputRate =
    rates.outputWeightOverrides?.[family] ?? rates.modelRates.outputRate;

  const weightedTokens =
    input.inputTokens * rates.modelRates.inputRate +
    input.outputTokens * outputRate +
    (input.cacheReadTokens ?? 0) * rates.modelRates.cacheReadRate +
    (input.cacheWriteTokens ?? 0) * rates.modelRates.cacheWriteRate;

  const credits = (weightedTokens / WEIGHTED_TOKENS_PER_CREDIT) * multiplier;

  return Math.round(credits * MICRO_CREDITS_PER_CREDIT);
};

/** Micro-credits → decimal credits, for display only — never fed back into the data layer. */
export const formatMicroCreditsAsCredits = (microCredits: number): number =>
  microCredits / MICRO_CREDITS_PER_CREDIT;

/**
 * Lookup-key prefix every one-time Stripe credit-pack price must carry (see
 * docs/AI_CREDITS_PLAN.md § "PR E"). `purchaseCredits` refuses any lookup key
 * without this prefix before Stripe is ever called, so the mutation can't be
 * pointed at an unrelated one-time price.
 */
export const STRIPE_CREDIT_PACK_LOOKUP_KEY_PREFIX = 'price_credit_pack_';

/** Stripe Price metadata key holding how many whole credits a pack grants — validated as a positive integer at purchase time, never trusted as-is. */
export const STRIPE_CREDIT_PACK_CREDITS_METADATA_KEY = 'credits';

export const isCreditPackLookupKey = (lookupKey: string): boolean =>
  lookupKey.startsWith(STRIPE_CREDIT_PACK_LOOKUP_KEY_PREFIX);
